import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { 
    Layout, 
    Splitter, 
    Tabs, 
    Typography,  
    Tag, 
    Form, 
    Input, 
    Button, 
    List, 
    Skeleton,
    Space,
    Card,
    Descriptions,
    Dropdown,
    Menu,
    Modal,
    Tooltip,
    Popconfirm,
    Divider,
    Badge
} from 'antd';
import {
    UserOutlined,
    TeamOutlined,
    FileTextOutlined,
    PhoneOutlined,
    MailOutlined,
    EditOutlined,
    MoreOutlined,
    DownloadOutlined,
    StarFilled,
    FilePdfFilled,
    PlusOutlined,
    StopOutlined,
    DeleteOutlined,
    ClockCircleOutlined,
    PartitionOutlined
} from '@ant-design/icons';
import PhoneInputUS from '../common/PhoneInputUS';
import PhoneDisplay from '../common/PhoneDisplay';
import { isWebchatMobile } from '../../utils/format';
import { theme, Select } from 'antd'; // Add Select
import _ from 'lodash';
import moment from 'moment';
import { phone as phoneLib } from 'phone';
import { evaluateTCPA } from '../../utils/tcpa'; // Import helper
import { useFeature } from '../common/FeatureGate';
import { languageOptions } from '../../utils/languages';

function normalizeMobileForValidation(raw) {
    if (typeof raw === 'object' && raw !== null) {
        const { countryCode, areaCode, phoneNumber } = raw;
        if (!areaCode && !phoneNumber) return '';
        return `+${countryCode || 1}${areaCode || ''}${phoneNumber || ''}`;
    }
    return typeof raw === 'string' ? raw : '';
}

const mobileFormatValidator = (_rule, value) => {
    const mobile = normalizeMobileForValidation(value);
    if (!mobile) return Promise.resolve(); // empty is allowed — user can clear mobile
    const result = phoneLib(mobile);
    if (!result.isValid) return Promise.reject('Please enter a valid mobile number');
    return Promise.resolve();
};

const OPTED_IN_OPTION = 0;
const OPTED_OUT_OPTION = 1;
const EXCLUDED_FIELDS = ['firstname', 'lastname', 'email', 'mobile', 'optedInDate', 'optedOutDate', 'agreement', 'notes', 'status'];

import useContactProfileStore from '../../stores/useContactProfileStore';
// Remove import { useStore as useInboxStore } from '../inbox-v2/inbox-store';
import {
    getUserMessageTemplates,
    getSequenceTemplates,
    checkUserGroupDefault,
    sendInterviewScheduling,
    sendReply
} from '../inbox-v2/resources';
import ThreadList from '../inbox-v2/components/Threads/ThreadList';
import MessageInputField from '../inbox-v2/components/MessageInputField/MessageInputField';
import { showError } from '../inbox-v2/utils/errorMessages';
import { useInboxV2Styles } from '../inbox-v2/inboxv2-styles';
import { socket } from '../../io';
import './UserProfile.css';
import UserProfileSkeleton from './UserProfileSkeleton';
import GroupsTab from './GroupsTab';
import ActivityTab from './ActivityTab';
import session from '../../utils/session';
import ContactEnrollmentHistory from '../flow-enrollment/ContactEnrollmentHistory';

// Placeholder styles until we refine them
const styles = {
    container: {
        height: 'calc(100vh - 60px)', // Adjust based on main layout header
        backgroundColor: '#fff',
        overflow: 'hidden'
    },
    leftPanel: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid #f0f0f0'
    },
    header: {
        padding: '16px 16px',
        borderBottom: '1px solid #f0f0f0',
        background: 'linear-gradient(110deg, #F6FFED 0%, #E6F7FF 100%)',
                                       
    },
    rightPanel: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    tagInput: {
        width: 64,
        height: 22,
        fontSize: '12px',
    },
    formLabel: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#263238',
    }
};

const UserProfile = ({ match }) => {
    const { contactId } = match.params;
    const {  
        contact, 
        loading, 
        savingDetails,
        savingAdditional,
        fetchContact, 
        groups, 
        threads, 
        loadingThreads, 
        fetchThreads,
        setActiveTab,
        downloadContactPDF,
        toggleContactStar,
        deleteContact,
        saveContactDetails,
        saveContactAdditionalInfo,
        addTag,
        removeTag,
        validateContactMobile,
        companyGroups,
        otherPrivateGroupCount,
        fetchCompanyGroups,
        translateThreadOnDemand
    } = useContactProfileStore();
    
    // Using styles from inbox-v2 for consistency in the right panel
    const { contentStyle, headerStyle, textingInputAreaStyle } = useInboxV2Styles({ hasScheduling: false });
    const [detailsForm] = Form.useForm();
    const [additionalForm] = Form.useForm();
    const [detailsChanged, setDetailsChanged] = useState(false);
    const [additionalChanged, setAdditionalChanged] = useState(false);
    const [activeTabKey, setActiveTabKey] = useState('details');
    const [optStatus, setOptStatus] = useState(null); // Add optStatus state
    const [initialOptStatus, setInitialOptStatus] = useState(null); // Track original status so we skip TCPA update when unchanged
    
    // Local state for MessageInputField requirements
    const [userMessageTemplates, setUserMessageTemplates] = useState([]);
    const [userSequenceTemplates, setUserSequenceTemplates] = useState([]);

    useEffect(() => {
        const fetchTemplates = async () => {
            const [templatesRes, seqRes] = await Promise.all([
                getUserMessageTemplates(),
                getSequenceTemplates(),
            ]);
            if (templatesRes.statusCode === 200) {
                setUserMessageTemplates(templatesRes.data);
            }
            const seqData = seqRes?.data || seqRes;
            setUserSequenceTemplates(seqData?.rows || []);
        };
        fetchTemplates();
    }, []);
    
    // PDF download
    const [pdfLoading, setPdfLoading] = useState(false);
    const handleDownloadPDF = async () => {
        setPdfLoading(true);
        try {
            await downloadContactPDF(contactId);
        } finally {
            setPdfLoading(false);
        }
    };

    // Tags State
    const { token } = theme.useToken();
    const [inputVisible, setInputVisible] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const inputRef = React.useRef(null);
    const [pendingTag, setPendingTag] = useState(null);

    useEffect(() => {
        if (inputVisible) {
            inputRef.current?.focus();
        }
    }, [inputVisible]);

    const handleInputConfirm = () => {
        if (inputValue && contact.tags && !contact.tags.some(tag => tag.value === inputValue)) {
            addTag(inputValue);
        }
        setInputVisible(false);
        setInputValue('');
    };

    useEffect(() => {
        // Declare outside `if` so cleanup can always reference them
        const handleMessage = (data) => useContactProfileStore.getState().handleSocketMessage(data);
        const handleFetchThreads = (thread) => useContactProfileStore.getState().handleFetchThreads(thread);
        // ADR-027 scheduled-message bubble events — same handlers the inbox uses
        // so the event-reminder bubble appears / follow-ups disappear live.
        const handleSchedCancelled = (payload) => useContactProfileStore.getState().applyScheduledMessageSocketEvent('scheduled_message:cancelled', payload);
        const handleSchedSent = (payload) => useContactProfileStore.getState().applyScheduledMessageSocketEvent('scheduled_message:sent', payload);
        const handleSchedRescheduled = (payload) => useContactProfileStore.getState().applyScheduledMessageSocketEvent('scheduled_message:rescheduled', payload);
        const handleSchedCreated = (payload) => useContactProfileStore.getState().applyScheduledMessageSocketEvent('scheduled_message:created', payload);
        const handleSchedDeleted = (payload) => useContactProfileStore.getState().applyScheduledMessageSocketEvent('scheduled_message:deleted', payload);

        if (contactId) {
            fetchContact(contactId);
            fetchThreads(contactId, 1);

            // Fetch company groups for the count badge
            const companyIdFromSession = session.get('userData') && session.get('userData').companyId;
            if (companyIdFromSession) {
                fetchCompanyGroups(companyIdFromSession, contactId);
            }

            socket.on('receiveMsg', handleMessage);
            socket.on('threadUpdate', handleMessage);
            socket.on('fetchThreads', handleFetchThreads);
            socket.on('scheduled_message:cancelled', handleSchedCancelled);
            socket.on('scheduled_message:sent', handleSchedSent);
            socket.on('scheduled_message:rescheduled', handleSchedRescheduled);
            socket.on('scheduled_message:created', handleSchedCreated);
            socket.on('scheduled_message:deleted', handleSchedDeleted);
        }

        return () => {
             // Leave socket rooms
             useContactProfileStore.getState().leaveRooms();

             // Pass specific refs so global io.js listeners are not removed
             socket.off('receiveMsg', handleMessage);
             socket.off('threadUpdate', handleMessage);
             socket.off('fetchThreads', handleFetchThreads);
             socket.off('scheduled_message:cancelled', handleSchedCancelled);
             socket.off('scheduled_message:sent', handleSchedSent);
             socket.off('scheduled_message:rescheduled', handleSchedRescheduled);
             socket.off('scheduled_message:created', handleSchedCreated);
             socket.off('scheduled_message:deleted', handleSchedDeleted);
        };
    }, [contactId]);

    useEffect(() => {
        if (contact) {
            detailsForm.setFieldsValue({
                firstname: contact.contactdetails?.find(d => d.field === 'firstname')?.value || '',
                lastname: contact.contactdetails?.find(d => d.field === 'lastname')?.value || '',
                email: contact.contactdetails?.find(d => d.field === 'email')?.value || '',
                mobile: contact.contactdetails?.find(d => d.field === 'mobile')?.value || '',
                language: contact.language || undefined,
            });
            setDetailsChanged(false);

            const customFields = {};
            contact.contactdetails?.forEach(d => {
                if (!EXCLUDED_FIELDS.includes(d.field)) {
                    customFields[d.field] = d.value;
                }
            });
            
            additionalForm.setFieldsValue({
                notes: contact.notes || '',
                ...customFields
            });
            setAdditionalChanged(false);

            // Determine initial Opt Status
            const fieldsForTCPA = {};
            contact.contactdetails?.forEach(d => {
                fieldsForTCPA[d.field] = d.value;
            });
             // Also need agreement if it exists in contactdetails
            try {
                 const evaluated = evaluateTCPA(fieldsForTCPA);
                 setOptStatus(evaluated);
                 setInitialOptStatus(evaluated);
            } catch (error) {
                console.log(error);
                setOptStatus(OPTED_OUT_OPTION); // Default fallback
                setInitialOptStatus(OPTED_OUT_OPTION);
            }
        }
    }, [contact, contact?.name, contact?.blacklist, detailsForm, additionalForm]);

    const [mobileStatus, setMobileStatus] = useState({ status: '', help: null });
    // ADR-035: contact language field is only meaningful with AI translation
    const canTranslate = useFeature('ai-translation');
    const [checkingMobile, setCheckingMobile] = useState(false);

    const handleSaveDetails = async (values) => {
        // Mobile Validation
        const rawMobile = values.mobile;
        let mobileNumber = '';
            
        if (typeof rawMobile === 'object' && rawMobile !== null) {
            const { countryCode, areaCode, phoneNumber } = rawMobile;
            // If strictly empty we might skip, but backend validation handles required logic if needed.
            // But here validation is only if they provided something or if we want to ensure uniqueness of non-empty.
            // If user clears mobile, we might just save.
            if (areaCode && phoneNumber) {
                 mobileNumber = `+${countryCode || 1}${areaCode || ''}${phoneNumber || ''}`;
            }
        } else {
             mobileNumber = rawMobile;
        }

        if (mobileNumber) {
            setCheckingMobile(true);
            setMobileStatus({ status: 'validating', help: 'Checking number...' });
            
            const res = await validateContactMobile(mobileNumber);
            setCheckingMobile(false);
            
            if (!res.isValid) {
                 if (res.reason === 'unique' && res.existingContact) {
                      setMobileStatus({ 
                          status: 'error', 
                          help: (
                              <span>
                                  This mobile number is already assigned to <a href={`/contacts/${res.existingContact.id}`}>{res.existingContact.name}</a>.
                              </span>
                          )
                      });
                 } else {
                      setMobileStatus({ status: 'error', help: res.message });
                 }
                 return; // Stop save
            } else {
                 setMobileStatus({ status: 'success', help: null });
            }
        } else {
            // Reset status if empty
            setMobileStatus({ status: '', help: null });
        }

        // Proceed to save
        await saveContactDetails(values);
    };

    const handleDetailsValuesChange = (changedValues, allValues) => {
        setDetailsChanged(true);
        // Clear error when user changes mobile input to allow retry
        if (changedValues.mobile) {
             setMobileStatus({ status: '', help: null });
        }
    };

    const handleAdditionalChange = () => {
        setAdditionalChanged(true);
    };

    const handleOptStatusChange = (value) => {
        setOptStatus(value);
        setAdditionalChanged(true);
    };

    const handleSaveAdditionalWithTCPA = (values) => {
         let date = moment().format('YYYY-MM-DD');
         let tcpaFields = {};

         // Only update TCPA fields when the status actually changed — otherwise
         // preserve the existing optedInDate/optedOutDate so the TCPA date stays put.
         if (optStatus !== initialOptStatus) {
             if (optStatus === OPTED_OUT_OPTION) {
                 tcpaFields = {
                     optedOutDate: date,
                     optedInDate: null
                 };
             } else if (optStatus === OPTED_IN_OPTION) {
                 tcpaFields = {
                     optedInDate: date,
                     optedOutDate: null
                 };
             }
         }

         const finalValues = {
             ...values,
             ...tcpaFields
         };

         saveContactAdditionalInfo(finalValues);
    };

    const handleResetDetails = () => {
        if (contact) {
            detailsForm.setFieldsValue({
                firstname: contact.contactdetails?.find(d => d.field === 'firstname')?.value || '',
                lastname: contact.contactdetails?.find(d => d.field === 'lastname')?.value || '',
                email: contact.contactdetails?.find(d => d.field === 'email')?.value || '',
                mobile: contact.contactdetails?.find(d => d.field === 'mobile')?.value || '',
                language: contact.language || undefined,
            });
            setDetailsChanged(false);
            setMobileStatus({ status: '', help: null });
        }
    };

    const handleUndoAdditional = () => {
         if (contact) {
            const customFields = {};
            contact.contactdetails?.forEach(d => {
                if (!EXCLUDED_FIELDS.includes(d.field)) {
                    customFields[d.field] = d.value;
                }
            });
            
            additionalForm.setFieldsValue({
                notes: contact.notes || '',
                ...customFields
            });
            
            // Re-evaluate TCPA from original contact data
            const fieldsForTCPA = {};
            contact.contactdetails?.forEach(d => {
                fieldsForTCPA[d.field] = d.value;
            });
            try {
                 const evaluated = evaluateTCPA(fieldsForTCPA);
                 setOptStatus(evaluated);
            } catch (error) {
                 setOptStatus(OPTED_OUT_OPTION);
            }
            
            setAdditionalChanged(false);
         }
    };

    const updateButtonStyle = (disabled) => ({
        transition: 'all 0.3s ease',
        backgroundColor: disabled ? '#d9d9d9' : '#4cb050',
        borderColor: disabled ? '#d9d9d9' : '#4cb050',
        color: disabled ? 'rgba(0, 0, 0, 0.25)' : '#fff',
    });

    if (loading || !contact) {
        return <UserProfileSkeleton />;
    }

    const renderDetailsTab = () => (
        <div style={{ paddingRight: 16 }}>
            <Form form={detailsForm} layout="vertical" onFinish={handleSaveDetails} onValuesChange={handleDetailsValuesChange}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#263238' }}>Contact Information</h3>
                <div style={{ display: 'flex', gap: 12, marginBottom: 0 }}>
                        <Form.Item 
                            label={<span style={styles.formLabel}>First Name</span>} 
                            name="firstname" 
                            style={{ flex: 1, marginBottom: 12 }}
                            rules={[{ required: true, message: 'First name is required' }]}
                        >
                            <Input prefix={<UserOutlined />} disabled={savingDetails || checkingMobile} />
                        </Form.Item>
                        <Form.Item 
                            label={<span style={styles.formLabel}>Last Name</span>} 
                            name="lastname" 
                            style={{ flex: 1, marginBottom: 12 }}
                            rules={[{ required: true, message: 'Last name is required' }]}
                        >
                            <Input prefix={<UserOutlined />} disabled={savingDetails || checkingMobile} />
                        </Form.Item>
                </div>
                
                <div style={{ display: 'flex', gap: 12 }}>
                        {isWebchatMobile(contact?.mobile) && !contact?.contactdetails?.find(d => d.field === 'mobile')?.value ? (
                            <div style={{ flex: 1, marginBottom: 12, display: 'flex', alignItems: 'center', minHeight: 62, paddingTop: 22 }}>
                                <PhoneDisplay value={contact.mobile} />
                            </div>
                        ) : (
                            <Form.Item
                                label={<span style={styles.formLabel}>Mobile</span>}
                                name="mobile"
                                style={{ flex: 1, marginBottom: 12 }}
                                rules={[{ validator: mobileFormatValidator }]}
                                validateStatus={mobileStatus.status || undefined}
                                help={mobileStatus.help || undefined}
                            >
                                <PhoneInputUS enableSearch disabled={checkingMobile || savingDetails}/>
                            </Form.Item>
                        )}
                        <Form.Item
                            label={<span style={styles.formLabel}>Email</span>}
                            name="email"
                            style={{ flex: 1, marginBottom: 12 }}
                            rules={[{ type: 'email', message: 'Invalid email format' }]}
                        >
                            <Input prefix={<MailOutlined />} disabled={savingDetails || checkingMobile} />
                        </Form.Item>
                </div>

                {/* ADR-035: the language this contact writes in. Detected from their first
                    message; set here it becomes manual and is never overwritten. */}
                {canTranslate && (
                    <div style={{ display: 'flex', gap: 12 }}>
                        <Form.Item
                            label={<span style={styles.formLabel}>Language{contact?.language_source === 'detected' ? ' (detected)' : ''}</span>}
                            name="language"
                            style={{ flex: 1, marginBottom: 12, maxWidth: 'calc(50% - 6px)' }}
                        >
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Not set"
                                options={languageOptions(contact?.language)}
                                disabled={savingDetails || checkingMobile}
                            />
                        </Form.Item>
                    </div>
                )}

                <div style={{ textAlign: 'right', marginTop: 8 }}>
                    {detailsChanged && !savingDetails && !checkingMobile && (
                         <Button onClick={handleResetDetails} style={{ marginRight: 8 }} size="small">
                             Reset
                         </Button>
                    )}
                    <Button 
                        htmlType="submit" 
                        loading={savingDetails || checkingMobile} 
                        size="small" 
                        disabled={!detailsChanged || savingDetails || checkingMobile || mobileStatus.status === 'error'} 
                        style={updateButtonStyle(!detailsChanged || savingDetails || checkingMobile || mobileStatus.status === 'error')}
                    >
                        Update
                    </Button>
                </div>
            </Form>

            <Divider style={{ margin: '12px 0' }} />

            <Form form={additionalForm} layout="vertical" onFinish={handleSaveAdditionalWithTCPA} onValuesChange={handleAdditionalChange} disabled={savingAdditional}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#263238' }}>Additional Information</h3>
                
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 2 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 50%', minWidth: '250px', display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
                             <div style={{ width: '130px', backgroundColor: '#fafafa', padding: '4px 11px', borderRight: '1px solid #f0f0f0', color: 'rgba(0, 0, 0, 0.88)', display: 'flex', alignItems: 'center' }}>TCPA Status</div>
                             <div style={{ flex: 1, padding: '4px 11px', display: 'flex', alignItems: 'center', borderRight: '1px solid #f0f0f0' }}>
                                <Select
                                    disabled={savingAdditional}
                                    value={optStatus}
                                    onChange={handleOptStatusChange}
                                    bordered={false}
                                    size="small"
                                    className={optStatus === OPTED_IN_OPTION ? 'tcpa-select-opted-in' : 'tcpa-select-opted-out'}
                                    style={{ fontWeight: 600, width: '100%' }}
                                >
                                    <Select.Option value={OPTED_IN_OPTION} style={{ color: '#4cb050', fontWeight: 'normal' }}>Opted In</Select.Option>
                                    <Select.Option value={OPTED_OUT_OPTION} style={{ color: '#ff4d4f', fontWeight: 'normal' }}>Opted Out</Select.Option>
                                </Select>
                             </div>
                        </div>
                        <div style={{ flex: '1 1 50%', minWidth: '250px', display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
                             <div style={{ width: '130px', backgroundColor: '#fafafa', padding: '4px 11px', borderRight: '1px solid #f0f0f0', color: 'rgba(0, 0, 0, 0.88)', display: 'flex', alignItems: 'center' }}>TCPA Date</div>
                             <div style={{ flex: 1, padding: '4px 11px', display: 'flex', alignItems: 'center' }}>
                                <span style={{ cursor: 'not-allowed', display: 'inline-block', width: '100%', whiteSpace: 'nowrap' }}>
                                    {(() => {
                                        const dateValue = contact.contactdetails?.find(d => d.field === 'optedOutDate')?.value || 
                                                        contact.contactdetails?.find(d => d.field === 'optedInDate')?.value;
                                        return dateValue ? moment(dateValue).format('MMM DD, YYYY') : '';
                                    })()}
                                </span>
                             </div>
                        </div>
                    </div>
                    {contact.contactdetails?.filter(d => !EXCLUDED_FIELDS.includes(d.field)).map((detail, index, arr) => (
                        <div key={detail.id} style={{ display: 'flex', borderBottom: index === arr.length - 1 ? 'none' : '1px solid #f0f0f0' }}>
                             <div style={{ minWidth: '130px', maxWidth: '200px', backgroundColor: '#fafafa', padding: '4px 11px', borderRight: '1px solid #f0f0f0', color: 'rgba(0, 0, 0, 0.88)', display: 'flex', alignItems: 'center', wordBreak: 'break-word' }}>{detail.field}</div>
                             <div style={{ flex: 1, padding: '4px 11px' }}>
                                <Form.Item name={detail.field} initialValue={detail.value} style={{ margin: 0 }}>
                                    <Input bordered={false} size="small" disabled={savingAdditional} />
                                </Form.Item>
                             </div>
                        </div>
                    ))}
                </div>

                <div style={{ textAlign: 'right', marginTop: 8 }}>
                     {additionalChanged && !savingAdditional && (
                         <Button onClick={handleUndoAdditional} style={{ marginRight: 8 }} size="small">
                             Undo
                         </Button>
                    )}
                    <Button htmlType="submit" loading={savingAdditional} size="small" disabled={!additionalChanged || savingAdditional} style={updateButtonStyle(!additionalChanged || savingAdditional)}>
                        Update
                    </Button>
                </div>
            </Form>
        </div>
    );

    // Company ID for groups tab
    const companyId = session.get('userData') && session.get('userData').companyId;

    const tabItems = [
        {
            key: 'details',
            label: 'Details',
            icon: <UserOutlined />,
            children: renderDetailsTab()
        },
        {
            key: 'groups',
            label: (
                <span>
                    Groups <Badge
                        count={(Array.isArray(companyGroups) ? companyGroups.filter(g => g.isMember && g.canEdit !== false).length : 0) + (otherPrivateGroupCount || 0)}
                        showZero
                        style={{ backgroundColor: '#52c41a', marginLeft: 6, fontSize: 12 }}
                    />
                </span>
            ),
            icon: <TeamOutlined />,
            children: <GroupsTab contact={contact} companyId={companyId} />
        },
        {
            key: 'activity',
            label: 'Activity',
            icon: <ClockCircleOutlined />,
            children: <ActivityTab contactId={contactId} active={activeTabKey === 'activity'} />
        },
        {
            key: 'enrollments',
            label: 'Enrollments',
            icon: <PartitionOutlined />,
            children: <ContactEnrollmentHistory contactId={contactId} />
        }
    ];

    const handleDelete = () => {
        Modal.confirm({
            title: 'Delete Contact',
            content: 'Are you sure you want to delete this contact?',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                const success = await deleteContact(contactId);
                if (success) {
                    // Redirect or handle post-delete
                    window.location.href = '/contacts'; 
                }
            }
        });
    };

    const renderTags = () => {
        // Adapt tags to inboxv2 structure if needed, assuming contact.tags is populated
        const tags = contact.tags || [];

        const tagPlusStyle = {
            height: 22,
            background: token.colorBgContainer,
            borderStyle: 'dashed'
        };

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', marginTop: 8, alignItems: 'start' }}>
                 {tags.map(tag => {
                     const isLongTag = tag.value.length > 20;
                     const tagElem = (
                        <Popconfirm
                            key={tag.id}
                            title={`Are you sure to remove "${tag.value}"?`}
                            onConfirm={() => removeTag(tag.id)}
                            onCancel={() => setPendingTag(null)}
                            okText="Yes"
                            cancelText="No"
                            open={pendingTag === tag.id}
                        >
                            <Tag
                                closable={!tag.tag_id} // Assuming tag_id present means mapped/system? Or strict boolean from InboxV2 logic
                                onClose={(e) => {
                                    e.preventDefault();
                                    setPendingTag(tag.id);
                                }}
                                style={{
                                    backgroundColor: '#F8FFFB',
                                    color: '#0F5132',
                                    fontSize: '12px',
                                    padding: '0 3px',
                                    borderRadius: 4,
                                    border: '1px solid #A7E6C5',
                                    cursor: 'default',
                                    userSelect: 'none',
                                }}
                            >
                                <span>{isLongTag ? `${tag.value.slice(0, 20)}...` : tag.value}</span>
                            </Tag>
                        </Popconfirm>
                     );
                     return isLongTag ? <Tooltip title={tag.value} key={tag.id}>{tagElem}</Tooltip> : tagElem;
                 })}
                 
                 {inputVisible ? (
                    <Input
                        ref={inputRef}
                        type="text"
                        size="small"
                        style={styles.tagInput}
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onBlur={handleInputConfirm}
                        onPressEnter={handleInputConfirm}
                    />
                 ) : (
                    <Tag style={tagPlusStyle} icon={<PlusOutlined />} onClick={() => setInputVisible(true)}>
                        New Tag
                    </Tag>
                 )}
            </div>
        );
    };

    return (
        <div style={styles.container}>
            <Splitter>
                <Splitter.Panel defaultSize="60%" min="30%" max="60%">
                    <div style={styles.leftPanel} className="user-profile-wrapper">
                        {/* Left Header */}
                         <div style={{ ...styles.header }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <Typography.Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {contact.blacklist && (
                                            <Tooltip title="Opted out" placement="top">
                                                <StopOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
                                            </Tooltip>
                                        )}
                                        {contact.name}
                                    </Typography.Title>
                                    {/* <Tag color={contact.status === 'active' ? 'green' : 'default'} style={{ marginTop: 4 }}>
                                        {contact.status || 'Unknown'}
                                    </Tag> */}
                                </div>
                                <Space>
                                    <Tooltip title={contact.star ? 'Remove Star' : 'Star'}>
                                        <Button 
                                            icon={<StarFilled />} 
                                            style={{ color: contact.star ? 'gold' : 'gray' }}
                                            onClick={toggleContactStar}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Download PDF">
                                        <Button icon={<FilePdfFilled />} loading={pdfLoading} onClick={handleDownloadPDF} style={{ color: 'gray' }} />
                                    </Tooltip>
                                    <Tooltip title="Delete Contact">
                                        <Button icon={<DeleteOutlined />} onClick={handleDelete} />
                                    </Tooltip>
                                </Space>
                            </div>
                            
                            {/* Tags Section */}
                            {renderTags()}
                         </div>
                        
                        {/* Tabs content */}
                         <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                             <Tabs
                                activeKey={activeTabKey}
                                onChange={setActiveTabKey}
                                tabPosition="left"
                                style={{ height: '100%', width: '100%' }}
                                items={tabItems.map(item => ({
                                    ...item,
                                    children: (
                                        <div className="user-profile-content-scroll">
                                            {item.children}
                                        </div>
                                    )
                                }))}
                             />
                         </div>
                    </div>
                </Splitter.Panel>
                
                <Splitter.Panel defaultSize="40%" min="40%">
                     <div style={styles.rightPanel}>
                        {/* Inbox-like Header */}
                        <div style={{ ...headerStyle, padding: '0 24px', display: 'flex', alignItems: 'center' }}>
                            <Typography.Title level={5} style={{ margin: 0 }}>Conversation</Typography.Title>
                        </div>
                        
                        {/* Threads */}
                        <div style={{ ...contentStyle, backgroundColor: '#fafafa', padding: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <ThreadList 
                                contactId={contactId} 
                                threads={threads} 
                                retrieveThreads={fetchThreads}
                                contact={contact}
                                onTranslateThread={translateThreadOnDemand}
                            />
                        </div>
                        
                        {/* Input Area */}
                        <div style={textingInputAreaStyle}>
                             <MessageInputField 
                                activeContactModel={contact}
                                sendMessage={async (body, botmapId, cId, cMobile, moduleGroup, translation, opts) => {
                                    const data = {
                                        body,
                                        botmapId,
                                        contactId: cId,
                                        moduleGroup: moduleGroup || '',
                                        sendType: 'reply',
                                        to: cMobile
                                    };
                                    // ADR-035: previewed translation → what the recruiter typed rides along
                                    if (translation) data.translation = translation;
                                    // ADR-025: channel routing + closed-window fallback
                                    if (opts && opts.channel) data.channel = opts.channel;
                                    if (opts && opts.fallback) data.fallback = opts.fallback;
                                    return await sendReply(data);
                                }}
                                userMessageTemplates={userMessageTemplates}
                                userSequenceTemplates={userSequenceTemplates}
                                userHasCalendar={async () => {
                                    try {
                                        const calendarInfo = await checkUserGroupDefault('calendar');
                                        if (calendarInfo.statusCode === 200) {
                                            return calendarInfo.data;
                                        }
                                        return [];
                                    } catch (error) {
                                        console.error('Failed to check user calendar:', error);
                                        showError('calendarCheck');
                                        return [];
                                    }
                                }}
                                sendCalendarInvite={async (contactMobile, cId, customMessage, schedulingUserId, translation) => {
                                    const payload = { to: contactMobile, contactId: cId };
                                    if (customMessage) {
                                        payload.customMessage = customMessage;
                                    }
                                    // Scheduling-manager: book on the chosen teammate's calendar.
                                    if (schedulingUserId) {
                                        payload.schedulingUserId = schedulingUserId;
                                    }
                                    if (translation) payload.translation = translation; // ADR-035
                                    return await sendInterviewScheduling(payload);
                                }}
                             />
                        </div>
                     </div>
                </Splitter.Panel>
            </Splitter>
        </div>
    );
};

export default withRouter(UserProfile);
